import { useState, useCallback } from "react";
import type { JSONContent } from "@tiptap/react";
import type { SubTemplateField } from "@/types/template";
import type { SubTemplateData } from "@/types/evaluation";
import { UnifiedTiptapEditor } from "@/components/unified/UnifiedTiptapEditor";
import { contentToNodes } from "@/components/unified/templateToDocument";
import { documentToContentNodes } from "@/components/unified/documentToContentNodes";

interface Props {
  field: SubTemplateField;
  data?: SubTemplateData;
  onChange: (data: SubTemplateData) => void;
}

interface EditorPanelProps {
  data: SubTemplateData;
  collapsedSections: Record<string, boolean>;
  setCollapsedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onChange: (data: SubTemplateData) => void;
  onDocumentChange: (doc: JSONContent) => void;
  initialDoc: JSONContent;
}

function makeToggleSection(data: SubTemplateData, onChange: (d: SubTemplateData) => void) {
  return (sectionId: string) => {
    onChange({
      ...data,
      sectionToggles: {
        ...data.sectionToggles,
        [sectionId]: !(data.sectionToggles[sectionId] ?? true),
      },
    });
  };
}

function SubTemplateEditorPanel({
  collapsedSections,
  data,
  initialDoc,
  onChange,
  onDocumentChange,
  setCollapsedSections,
}: Readonly<EditorPanelProps>) {
  return (
    <UnifiedTiptapEditor
      initialContent={initialDoc}
      values={data.values}
      setFieldValue={(fId, value) => {
        onChange({ ...data, values: { ...data.values, [fId]: value } });
      }}
      fillVariables={{}}
      interpolate={(t) => t}
      subTemplateValues={data.subTemplateValues}
      setSubTemplateValue={(fId, subData) => {
        onChange({ ...data, subTemplateValues: { ...data.subTemplateValues, [fId]: subData } });
      }}
      sectionToggles={data.sectionToggles}
      onToggleSection={makeToggleSection(data, onChange)}
      collapsedSections={collapsedSections}
      onCollapseSection={(sectionId) => {
        setCollapsedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
      }}
      fieldToggles={{}}
      onToggleField={() => {
        /* no-op */
      }}
      onDocumentChange={onDocumentChange}
    />
  );
}

function useDocumentChangeHandler(
  data: SubTemplateData | undefined,
  onChange: (data: SubTemplateData) => void,
) {
  return useCallback(
    (doc: JSONContent) => {
      if (!data) {
        return;
      }
      const nodes = documentToContentNodes(doc);
      const sectionToggles = { ...data.sectionToggles };
      for (const n of nodes) {
        if (n.type === "section" && !(n.id in sectionToggles)) {
          sectionToggles[n.id] = true;
        }
      }
      onChange({ ...data, content: nodes, sectionToggles });
    },
    [data, onChange],
  );
}

export function SubTemplateRenderer({ data, field, onChange }: Readonly<Props>) {
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [initialDoc] = useState<JSONContent>(() => ({
    content: data ? contentToNodes(data.content) : [],
    type: "doc",
  }));
  const handleDocumentChange = useDocumentChangeHandler(data, onChange);

  if (!data?.content) {
    return (
      <>
        <label>{field.label}</label>
        <div style={{ color: "#999" }}>Sub-template not found</div>
      </>
    );
  }

  return (
    <div className="sub-template-wrapper">
      <div
        className="sub-template-header"
        onClick={() => {
          setCollapsed(!collapsed);
        }}
      >
        <span className="collapse-indicator">{collapsed ? "▶" : "▼"}</span>
        <span className="sub-template-title">{data.templateName || field.label}</span>
      </div>
      {!collapsed && (
        <div className="sub-template-editor">
          <SubTemplateEditorPanel
            data={data}
            collapsedSections={collapsedSections}
            setCollapsedSections={setCollapsedSections}
            onChange={onChange}
            onDocumentChange={handleDocumentChange}
            initialDoc={initialDoc}
          />
        </div>
      )}
    </div>
  );
}
